import { useLayoutEffect, useRef, useState } from 'react';
import type { Integration, IntegrationStatus } from '@lizrd/contracts';
import { PROVIDER_ACCENT, integrationIdentifier } from '../lib/graph-visuals';
import { Mark } from './brand/Logo';
import { ProviderLogo } from './ProviderLogo';
import { Search, Lightbulb, type LucideIcon } from 'lucide-react';

// Short "what kind of source" label shown in the compact/collapsed state.
const PROVIDER_LABEL: Record<string, string> = {
  GITHUB: 'GitHub',
  GITLAB: 'GitLab',
  AWS: 'AWS',
  GCP: 'GCP',
  AZURE: 'Azure',
  SANDBOX: 'Sandbox',
};

const HUB = 'var(--iris)'; // brand green — theme-aware

// Vertical layout constants (px). Rows are compact so ~10+ sources fit without
// crowding; a source's full details are revealed on hover (see IntegrationNode).
const NODE_W = 190;
const ROW_H = 52;
const COL_CENTER_X = 116; // centre of the left column
const MIN_HEIGHT = 360;
const OUTPUT_W = 152; // outcome boxes on the right
const HUB_X_FRAC = 0.5; // hub sits centre so inputs (left) and outcomes (right) balance
const HUB_W = 128; // width of the tall central Domain column
const HUB_V_MARGIN = 18; // gap above/below the tall hub
const OUTCOME_GAP = 58; // vertical offset of each outcome box from the centre line

// What the domain produces — shown to the right of the hub so the picture
// reads sources → Domain → outcomes. Reused as the value promise during
// onboarding, before anything is connected.
const OUTCOMES = [
  { icon: Search, label: 'Cost Transparency', sub: 'where cost originates' },
  { icon: Lightbulb, label: 'Optimization Guidance', sub: 'actionable savings' },
] as const;

const isFlowing = (s: IntegrationStatus) => s === 'CONNECTED' || s === 'SYNCING';
const isError = (s: IntegrationStatus) => s === 'ERROR' || s === 'WARNING';

// A row in the left column is either a connected integration or the single
// generic "connect a source" affordance at the bottom.
type Row =
  | { kind: 'integration'; key: string; integration: Integration }
  | { kind: 'connect'; key: 'connect' };

/**
 * The interactive "data-flow" graph for the domain Settings tab. Connected
 * sources stack in a vertical column on the left and stream their data into a
 * central Domain hub along animated edges. Click a connected node for its
 * details/actions; a single "Connect source" node opens the provider picker so
 * any number of sources (including several AWS accounts) can be added.
 */
export function IntegrationFlow({
  integrations,
  envName,
  resourceCount,
  canManage,
  onSelect,
  onConnect,
}: {
  integrations: Integration[];
  envName: string;
  resourceCount?: number;
  canManage: boolean;
  onSelect: (integration: Integration) => void;
  onConnect: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver((entries) => setWidth(entries[0]!.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const rows: Row[] = [
    ...integrations.map((i): Row => ({ kind: 'integration', key: i.id, integration: i })),
    ...(canManage ? [{ kind: 'connect', key: 'connect' } as Row] : []),
  ];

  // Height grows with the column so nothing crowds. The hub is a tall column
  // spanning the full height; both groups centre on the mid-line, so every
  // connector is a straight horizontal line into the hub's edge.
  const height = Math.max(MIN_HEIGHT, rows.length * ROW_H + 72);
  const centerY = height / 2;
  const hubX = width * HUB_X_FRAC;
  const hubLeftX = hubX - HUB_W / 2;
  const hubRightX = hubX + HUB_W / 2;
  const startY = centerY - ((rows.length - 1) * ROW_H) / 2;
  const placed = rows.map((row, i) => ({ ...row, x: COL_CENTER_X, y: startY + i * ROW_H }));

  // Outcome boxes on the right, stacked around the centre, fed by value flowing
  // out of the hub. Animated only when a source is actually flowing.
  const outX = width - OUTPUT_W / 2 - 16;
  const outcomes = OUTCOMES.map((o, i) => ({
    ...o,
    y: centerY + (i === 0 ? -OUTCOME_GAP : OUTCOME_GAP),
  }));
  const anyFlowing = integrations.some((i) => isFlowing(i.status));

  return (
    <div
      ref={ref}
      className="relative w-full overflow-hidden rounded-lg border border-hairline bg-surface"
      style={{
        height,
        backgroundImage: `radial-gradient(circle at ${HUB_X_FRAC * 100}% 50%, color-mix(in srgb, var(--iris) 8%, transparent), transparent 60%)`,
      }}
    >
      {width > 0 && (
        <>
          {/* Edges */}
          <svg className="pointer-events-none absolute inset-0" width={width} height={height}>
            {placed.map((row) => {
              if (row.kind !== 'integration') {
                // Faint idle connector for the "connect source" affordance.
                return (
                  <path
                    key={row.key}
                    d={`M ${row.x + NODE_W / 2 - 8} ${row.y} L ${hubLeftX} ${row.y}`}
                    stroke="var(--hairline)"
                    strokeWidth={1}
                    strokeDasharray="2 8"
                    fill="none"
                    opacity={0.4}
                  />
                );
              }
              const s = row.integration.status;
              const connected = isFlowing(s);
              const errored = isError(s);
              const syncing = s === 'SYNCING';
              const accent = PROVIDER_ACCENT[row.integration.provider] ?? '#8b93a7';
              const color = errored ? 'var(--danger)' : connected ? accent : 'var(--hairline)';
              const d = `M ${row.x + NODE_W / 2 - 8} ${row.y} L ${hubLeftX} ${row.y}`;
              return (
                <g key={row.key}>
                  <path d={d} stroke={color} strokeWidth={1.25} fill="none" opacity={0.22} />
                  {connected && (
                    <path
                      d={d}
                      stroke={color}
                      strokeWidth={2.25}
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray="3 13"
                      className="lz-flow-line"
                      style={{ animationDuration: syncing ? '0.7s' : '1.5s' }}
                    />
                  )}
                </g>
              );
            })}

            {/* Value streaming out of the hub to the outcomes */}
            {outcomes.map((o) => {
              const d = `M ${hubRightX} ${o.y} L ${outX - OUTPUT_W / 2 + 8} ${o.y}`;
              return (
                <g key={`edge-${o.label}`}>
                  <path d={d} stroke="var(--iris)" strokeWidth={1.25} fill="none" opacity={0.22} />
                  {anyFlowing ? (
                    <path
                      d={d}
                      stroke="var(--iris)"
                      strokeWidth={2.25}
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray="3 13"
                      className="lz-flow-line"
                      style={{ animationDuration: '1.6s' }}
                    />
                  ) : (
                    <path
                      d={d}
                      stroke="var(--hairline)"
                      strokeWidth={1}
                      strokeDasharray="2 8"
                      fill="none"
                      opacity={0.4}
                    />
                  )}
                </g>
              );
            })}
          </svg>

          {/* Domain hub — a tall central column so every connector is a
              straight horizontal line into its edge. */}
          <div
            className="absolute flex flex-col items-center justify-center rounded-2xl px-3 text-center"
            style={{
              left: hubLeftX,
              top: HUB_V_MARGIN,
              width: HUB_W,
              height: height - HUB_V_MARGIN * 2,
              background:
                'linear-gradient(180deg, color-mix(in srgb, var(--iris) 22%, var(--surface-2)), var(--surface-2) 42%, var(--surface-2) 58%, color-mix(in srgb, var(--iris) 22%, var(--surface-2)))',
              border: `1.5px solid ${HUB}`,
              boxShadow: `0 0 34px -8px ${HUB}`,
            }}
          >
            <div className="flex justify-center" aria-hidden>
              <Mark size={34} />
            </div>
            <div className="mt-1.5 line-clamp-2 font-display text-sm font-bold text-bone">
              {envName}
            </div>
            <div className="mt-0.5 text-[0.6rem] uppercase tracking-wide text-muted">Domain</div>
            {resourceCount != null && resourceCount > 0 && (
              <div className="mt-1 text-[0.65rem] text-iris">{resourceCount} resources</div>
            )}
          </div>

          {/* Left column: connected sources + the connect affordance */}
          {placed.map((row) =>
            row.kind === 'integration' ? (
              <IntegrationNode key={row.key} x={row.x} y={row.y} node={row} onSelect={onSelect} />
            ) : (
              <ConnectNode key={row.key} x={row.x} y={row.y} onConnect={onConnect} />
            ),
          )}

          {/* Right column: what the domain produces */}
          {outcomes.map((o) => (
            <OutcomeNode key={o.label} x={outX} y={o.y} icon={o.icon} label={o.label} sub={o.sub} />
          ))}
        </>
      )}
    </div>
  );
}

// Centre the node on (x, y) via translate so it grows symmetrically on hover —
// keeping the connector, which meets the row's centre-line, anchored as the box
// expands. (No Tailwind transform utilities on these nodes, so this inline
// transform isn't overridden.)
const NODE_STYLE = (x: number, y: number) =>
  ({ left: x, top: y, width: NODE_W, transform: 'translate(-50%, -50%)' }) as const;

function IntegrationNode({
  x,
  y,
  node,
  onSelect,
}: {
  x: number;
  y: number;
  node: { integration: Integration };
  onSelect: (i: Integration) => void;
}) {
  const { integration } = node;
  const accent = PROVIDER_ACCENT[integration.provider] ?? '#8b93a7';
  const providerLabel = PROVIDER_LABEL[integration.provider] ?? integration.provider;
  const s = integration.status;
  const errored = isError(s);
  const identifier = integrationIdentifier(integration);
  const dot = errored
    ? 'var(--danger)'
    : s === 'SYNCING'
      ? 'var(--info)'
      : s === 'CONNECTED'
        ? accent
        : 'var(--muted)';
  const statusText = s === 'SYNCING' ? 'syncing…' : errored ? 'needs attention' : 'flowing';
  const billing = integration.billingStatus;
  const billingWarn =
    billing === 'error'
      ? 'billing issue'
      : billing === 'not_configured'
        ? 'billing not set up'
        : billing === 'pending'
          ? 'billing pending'
          : null;
  // An actionable billing problem (error / not set up) gets a subtle dot even when
  // collapsed, so it isn't hidden until hover; `pending` is informational so it stays
  // hover-only.
  const billingNeedsAttention = billing === 'error' || billing === 'not_configured';

  return (
    <button
      onClick={() => onSelect(integration)}
      title={`${integration.displayName} — ${s.toLowerCase()}`}
      className="group absolute flex items-center gap-2.5 rounded-lg border bg-surface-2 px-2.5 py-1.5 text-left transition-shadow hover:z-20 hover:shadow-lg"
      style={{
        ...NODE_STYLE(x, y),
        borderColor: errored ? 'var(--danger)' : `color-mix(in srgb, ${accent} 60%, transparent)`,
        boxShadow: `0 0 18px -8px ${errored ? 'var(--danger)' : accent}`,
      }}
    >
      <ProviderLogo provider={integration.provider} size={30} className="shrink-0" />
      <span className="min-w-0 flex-1">
        {/* Line 1 — collapsed: the source KIND; on hover: the full display name. */}
        <span className="block text-xs font-semibold text-bone">
          <span className="line-clamp-1 group-hover:hidden">{providerLabel}</span>
          <span className="hidden break-all group-hover:block">{integration.displayName}</span>
        </span>
        {/* Identifier — hover only. */}
        {identifier && (
          <span className="hidden break-all font-mono text-[0.6rem] text-muted group-hover:block">
            {identifier}
          </span>
        )}
        {/* Status — always visible (dot + label). A subtle amber dot flags an
            actionable billing problem while collapsed; the full billing line shows on hover. */}
        <span className="mt-0.5 flex items-center gap-1 text-[0.6rem] text-muted">
          <span
            className="inline-block h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: dot }}
          />
          <span className="truncate">{statusText}</span>
          {billingNeedsAttention && (
            <span
              className="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--warning)] group-hover:hidden"
              title="Billing needs attention"
            />
          )}
        </span>
        {/* Billing detail — hover only. */}
        {billingWarn && (
          <span
            className={`mt-0.5 hidden items-center gap-1 text-[0.6rem] font-medium group-hover:flex ${billing === 'pending' ? 'text-muted' : 'text-warning'}`}
          >
            <span
              className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${billing === 'pending' ? 'bg-[var(--muted)]' : 'bg-[var(--warning)]'}`}
            />
            {billingWarn}
          </span>
        )}
      </span>
    </button>
  );
}

function ConnectNode({ x, y, onConnect }: { x: number; y: number; onConnect: () => void }) {
  return (
    <button
      onClick={onConnect}
      title="Connect a new source"
      className="group absolute flex items-center justify-center gap-2 rounded-lg border border-dashed border-border px-2.5 py-1.5 text-center transition-colors hover:border-iris/60 hover:bg-surface-2"
      style={NODE_STYLE(x, y)}
    >
      <span className="grid h-6 w-6 place-items-center rounded-full border border-dashed border-muted text-sm text-muted transition-colors group-hover:border-iris group-hover:text-iris">
        ＋
      </span>
      <span className="text-xs font-semibold text-haze group-hover:text-bone">Connect source</span>
    </button>
  );
}

// A non-interactive "outcome" tile on the right — what the domain delivers.
// Iris-accented to distinguish produced value from the source inputs on the left.
function OutcomeNode({
  x,
  y,
  icon: Icon,
  label,
  sub,
}: {
  x: number;
  y: number;
  icon: LucideIcon;
  label: string;
  sub: string;
}) {
  return (
    <div
      className="absolute flex items-center gap-2.5 rounded-lg border bg-surface-2 p-2.5"
      style={{
        left: x,
        top: y,
        width: OUTPUT_W,
        marginLeft: -OUTPUT_W / 2,
        marginTop: -30,
        borderColor: 'color-mix(in srgb, var(--iris) 55%, transparent)',
        boxShadow: `0 0 18px -8px ${HUB}`,
      }}
    >
      <Icon className="h-5 w-5 shrink-0 text-iris" aria-hidden />
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 block text-xs font-semibold text-bone">{label}</span>
        <span className="mt-0.5 line-clamp-1 block text-[0.6rem] text-muted">{sub}</span>
      </span>
    </div>
  );
}
